/**
 * PricingSettingsModal — edits the live demo pricing rate card. Every change
 * writes the store immediately, so the StatsModal / CapacityPanel revenue
 * figures react in real time (great for "watch this" moments on a sales call).
 * Demo-local: no DB, resets to defaults each session.
 */
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { useEventPlannerStore } from '../store/eventPlannerStore'
import {
  type EventPricing,
  type TierFeeMatrix,
  type Tier,
  TIER_ORDER,
  TIER_LABELS,
  OPERATOR_CLASS_LABELS,
  PROGRAM_LABELS,
  BILLABLE_SERVICES,
  SERVICE_LABELS,
} from '../data/eventPricing'
import type { OperatorClass, ProgramMembership } from '../engine/types'
import { InfoDot } from './charts'

const FEE_COLUMNS: Array<{ key: keyof TierFeeMatrix; label: string; info: string }> = [
  { key: 'landing', label: 'Landing', info: 'One-time fee charged on arrival, by aircraft tier.' },
  { key: 'ramp', label: 'Ramp / nt', info: 'Daily ramp / parking position fee, charged per night on the ground.' },
  { key: 'overnight', label: 'Overnight / nt', info: 'Overnight tie-down surcharge, charged per night.' },
  { key: 'hangar', label: 'Hangar / nt', info: 'Hangar fee per night — billed only for zones marked “bill as hangar”.' },
]

const GROUP_INFO: Record<OperatorClass, string> = {
  fractional: 'Fleet operators (NetJets, Flexjet…) — typically the deepest negotiated discount.',
  management: 'Aircraft management companies — a moderate negotiated discount.',
  individual: 'Owner-flown / family office — usually full rate.',
}

const PROGRAM_INFO: Record<ProgramMembership, string> = {
  transient: 'Walk-up, no program — pays the full rate.',
  contract_fuel: 'Contract-fuel program — discounted fuel margin.',
  based_tenant: 'Based tenant / annual lease — ramp + overnight waived.',
  loyalty: 'Loyalty member — flat discount off the size-based fees.',
}

export function PricingSettingsModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const pricing = useEventPlannerStore((s) => s.pricing)
  const setPricing = useEventPlannerStore((s) => s.setPricing)

  const setFee = (fee: keyof TierFeeMatrix, tier: Tier, val: number) =>
    setPricing({ ...pricing, fees: { ...pricing.fees, [fee]: { ...pricing.fees[fee], [tier]: val } } })
  const setFuel = (k: keyof EventPricing['fuel'], val: number) =>
    setPricing({ ...pricing, fuel: { ...pricing.fuel, [k]: val } })
  const setService = (k: string, val: number) =>
    setPricing({ ...pricing, services: { ...pricing.services, [k]: val } })
  const setTop = (k: 'surgeMultiplier' | 'eventHandlingFee' | 'vipPremium', val: number) =>
    setPricing({ ...pricing, [k]: val })
  const setGroupDiscount = (g: OperatorClass, pct: number) =>
    setPricing({ ...pricing, clientGroupDiscount: { ...pricing.clientGroupDiscount, [g]: pct / 100 } })
  const setProgram = (p: ProgramMembership, patch: Partial<EventPricing['programModifiers'][ProgramMembership]>) =>
    setPricing({ ...pricing, programModifiers: { ...pricing.programModifiers, [p]: { ...pricing.programModifiers[p], ...patch } } })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-slate-950 text-white sm:max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogTitle className="text-base font-bold text-white">Event Pricing</DialogTitle>
        <p className="-mt-1 text-[11px] text-white/40">Live rate card — edits update revenue instantly. Resets each session.</p>

        <Tabs defaultValue="fees" className="mt-2">
          <TabsList className="flex-wrap bg-white/5">
            <TabsTrigger value="fees">Fees by Tier</TabsTrigger>
            <TabsTrigger value="event">Event / Surge</TabsTrigger>
            <TabsTrigger value="fuel">Fuel</TabsTrigger>
            <TabsTrigger value="services">Services</TabsTrigger>
            <TabsTrigger value="groups">Client Groups</TabsTrigger>
            <TabsTrigger value="programs">Programs</TabsTrigger>
          </TabsList>

          {/* Fees by tier matrix */}
          <TabsContent value="fees">
            <div className="grid grid-cols-[5rem_repeat(4,1fr)] gap-1.5 text-[11px]">
              <div className="text-white/40">Tier</div>
              {FEE_COLUMNS.map((c) => (
                <div key={c.key} className="flex items-center justify-center gap-1 text-white/40">
                  {c.label}
                  <InfoDot text={c.info} />
                </div>
              ))}
              {TIER_ORDER.map((tier) => (
                <FeeRow key={tier} tier={tier} pricing={pricing} onChange={setFee} />
              ))}
            </div>
            <p className="mt-2 text-[10px] text-white/30">USD. Landing is one-time per arrival; ramp/overnight/hangar bill per night.</p>
          </TabsContent>

          {/* Event / surge */}
          <TabsContent value="event" className="space-y-3">
            <Field label="Surge multiplier" info="Multiplier applied to landing / ramp / overnight / hangar — the Masters event premium.">
              <Num value={pricing.surgeMultiplier} step={0.1} onChange={(v) => setTop('surgeMultiplier', v)} suffix="×" />
            </Field>
            <Field label="Event handling / slot fee" info="Flat per-aircraft PPR / handling fee added during the event.">
              <Num value={pricing.eventHandlingFee} prefix="$" onChange={(v) => setTop('eventHandlingFee', v)} />
            </Field>
            <Field label="VIP premium" info="Flat surcharge added per aircraft flagged VIP.">
              <Num value={pricing.vipPremium} prefix="$" onChange={(v) => setTop('vipPremium', v)} />
            </Field>
          </TabsContent>

          {/* Fuel */}
          <TabsContent value="fuel" className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Jet-A margin / gal" info="Profit margin per gallon of Jet-A uplift, up to the high-volume threshold."><Num value={pricing.fuel.jetAMarginPerGal} step={0.05} prefix="$" onChange={(v) => setFuel('jetAMarginPerGal', v)} /></Field>
              <Field label="Avgas margin / gal" info="Profit margin per gallon of Avgas (100LL) — piston aircraft."><Num value={pricing.fuel.avgasMarginPerGal} step={0.05} prefix="$" onChange={(v) => setFuel('avgasMarginPerGal', v)} /></Field>
              <Field label="High-volume threshold" info="Gallons above which the high-volume Jet-A margin applies (a volume discount)."><Num value={pricing.fuel.volumeThresholdGal} step={50} suffix="gal" onChange={(v) => setFuel('volumeThresholdGal', v)} /></Field>
              <Field label="High-volume margin / gal" info="Jet-A margin for uplift beyond the threshold — usually lower than the base margin."><Num value={pricing.fuel.highVolumeMarginPerGal} step={0.05} prefix="$" onChange={(v) => setFuel('highVolumeMarginPerGal', v)} /></Field>
              <Field label="Uplift waiver" info="A single uplift at or above this many gallons waives the landing + ramp fee (a classic fuel incentive)."><Num value={pricing.fuel.upliftWaiverGal} step={50} suffix="gal" onChange={(v) => setFuel('upliftWaiverGal', v)} /></Field>
            </div>
          </TabsContent>

          {/* Services */}
          <TabsContent value="services" className="space-y-3">
            <p className="text-[11px] text-white/40">Flat fee charged once per occurrence when the service is requested.</p>
            <div className="grid grid-cols-2 gap-3">
              {BILLABLE_SERVICES.map((svc) => (
                <Field key={svc} label={SERVICE_LABELS[svc] ?? svc}>
                  <Num value={pricing.services[svc] ?? 0} prefix="$" onChange={(v) => setService(svc, v)} />
                </Field>
              ))}
            </div>
          </TabsContent>

          {/* Client groups */}
          <TabsContent value="groups" className="space-y-3">
            <p className="text-[11px] text-white/40">Negotiated discount off the size-based fees (landing / ramp / overnight / hangar).</p>
            {(Object.keys(OPERATOR_CLASS_LABELS) as OperatorClass[]).map((g) => (
              <Field key={g} label={OPERATOR_CLASS_LABELS[g]} info={GROUP_INFO[g]}>
                <Num value={Math.round(pricing.clientGroupDiscount[g] * 100)} suffix="% off" onChange={(v) => setGroupDiscount(g, v)} />
              </Field>
            ))}
          </TabsContent>

          {/* Programs */}
          <TabsContent value="programs" className="space-y-2">
            <p className="text-[11px] text-white/40">Membership / contract entitlements.</p>
            {(Object.keys(PROGRAM_LABELS) as ProgramMembership[]).map((p) => {
              const mod = pricing.programModifiers[p]
              return (
                <div key={p} className="rounded-lg border border-white/10 bg-black/20 p-2.5">
                  <div className="mb-1.5 flex items-center gap-1 text-xs font-semibold text-white/80">
                    {PROGRAM_LABELS[p]}
                    <InfoDot text={PROGRAM_INFO[p]} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Fuel margin" info="Percent of the standard fuel margin this program pays (e.g. 55% = a 45% fuel discount).">
                      <Num value={Math.round(mod.fuelMarginMultiplier * 100)} suffix="%" onChange={(v) => setProgram(p, { fuelMarginMultiplier: v / 100 })} />
                    </Field>
                    <Field label="Flat discount" info="Percent off landing / ramp / overnight / hangar for this program.">
                      <Num value={Math.round(mod.flatDiscount * 100)} suffix="% off" onChange={(v) => setProgram(p, { flatDiscount: v / 100 })} />
                    </Field>
                  </div>
                  <label className="mt-2 flex items-center gap-2 text-[11px] text-white/70">
                    <input type="checkbox" checked={mod.waiveRampOvernight} onChange={(e) => setProgram(p, { waiveRampOvernight: e.target.checked })} />
                    Waive ramp + overnight
                  </label>
                </div>
              )
            })}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

function FeeRow({ tier, pricing, onChange }: { tier: Tier; pricing: EventPricing; onChange: (fee: keyof TierFeeMatrix, tier: Tier, val: number) => void }) {
  return (
    <>
      <div className="flex items-center text-white/70">{TIER_LABELS[tier]}</div>
      {FEE_COLUMNS.map((c) => (
        <input
          key={c.key}
          type="number"
          value={pricing.fees[c.key][tier]}
          onChange={(e) => onChange(c.key, tier, Number(e.target.value) || 0)}
          className="w-full rounded border border-white/10 bg-black/40 px-1.5 py-1 text-center text-[11px] tabular-nums text-white/90 outline-none focus:border-sky-400/50"
        />
      ))}
    </>
  )
}

function Field({ label, hint, info, children }: { label: string; hint?: string; info?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-white/40">
        {label}
        {info && <InfoDot text={info} />}
      </div>
      {children}
      {hint && <div className="text-[10px] text-white/30">{hint}</div>}
    </div>
  )
}

function Num({
  value,
  onChange,
  step = 1,
  prefix,
  suffix,
}: {
  value: number
  onChange: (v: number) => void
  step?: number
  prefix?: string
  suffix?: string
}) {
  return (
    <div className={cn('flex items-center gap-1 rounded border border-white/10 bg-black/40 px-2 py-1', 'focus-within:border-sky-400/50')}>
      {prefix && <span className="text-[11px] text-white/40">{prefix}</span>}
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="w-full bg-transparent text-xs tabular-nums text-white/90 outline-none"
      />
      {suffix && <span className="shrink-0 text-[11px] text-white/40">{suffix}</span>}
    </div>
  )
}
